export interface DebugEvent<T = any> { ts: number; channel: string; phase?: string; message: string; level?: 'info' | 'warn' | 'error'; detail?: T; coalesceKey?: string }
export interface DebugBusConfig { maxPerChannel?: number }
type Listener = (events: DebugEvent[], newEvent?: DebugEvent) => void;

class DebugBus {
  private channels = new Map<string, DebugEvent[]>();
  private listeners = new Map<string, Set<Listener>>();
  private globalListeners = new Set<Listener>();
  constructor(private cfg: DebugBusConfig = {}) {}
  private getLimit() { return this.cfg.maxPerChannel ?? 1000; }
  push(evt: Omit<DebugEvent, 'ts'> & { ts?: number }) {
    const full: DebugEvent = { ts: evt.ts ?? Date.now(), ...evt } as DebugEvent;
    const list = this.channels.get(full.channel) ?? [];
    if (full.coalesceKey) {
      // Find last index with same coalesceKey (scan backwards for efficiency)
      for (let i = list.length - 1; i >= 0; i--) {
        const existing = list[i];
        if (existing.coalesceKey === full.coalesceKey) { list[i] = full; // replace
          // Notify listeners with updated list + event (treated as new event)
          const limit = this.getLimit();
          if (list.length > limit) list.splice(0, list.length - limit);
          this.channels.set(full.channel, list);
          this.listeners.get(full.channel)?.forEach(l => { try { l([...list], full); } catch {} });
          this.globalListeners.forEach(l => { try { l([...list], full); } catch {} });
          return;
        }
      }
    }
    list.push(full);
    const limit = this.getLimit();
    if (list.length > limit) list.splice(0, list.length - limit);
    this.channels.set(full.channel, list);
    this.listeners.get(full.channel)?.forEach(l => { try { l([...list], full); } catch {} });
    this.globalListeners.forEach(l => { try { l([...list], full); } catch {} });
  }
  getEvents(ch: string) { return [...(this.channels.get(ch) ?? [])]; }
  getChannels() { return [...this.channels.keys()].sort(); }
  clear(ch?: string) {
    if (ch) { this.channels.set(ch, []); this.listeners.get(ch)?.forEach(l=>{ try { l([], undefined); } catch {} }); return; }
    this.channels.forEach((_v,k)=> { this.channels.set(k, []); this.listeners.get(k)?.forEach(l=>{ try { l([], undefined); } catch {} }); });
    this.globalListeners.forEach(l=> { try { l([], undefined); } catch {} });
  }
  subscribe(ch: string, listener: Listener) {
    let set = this.listeners.get(ch); if(!set){ set = new Set(); this.listeners.set(ch, set);} set.add(listener); try { listener(this.getEvents(ch)); } catch {}
    return () => { set?.delete(listener); };
  }
  subscribeAll(listener: Listener) { this.globalListeners.add(listener); return () => { this.globalListeners.delete(listener); }; }
}

export const debugBus = (() => {
  if (typeof window !== 'undefined') {
    const w = window as any; if (!w.__DEBUG_BUS__) w.__DEBUG_BUS__ = new DebugBus({ maxPerChannel: 600 }); return w.__DEBUG_BUS__ as DebugBus;
  }
  return new DebugBus({ maxPerChannel: 600 });
})();

export function logDebug(channel: string, message: string, opts?: Partial<Omit<DebugEvent,'ts'|'channel'|'message'>>) { debugBus.push({ channel, message, ...opts }); }

export type { DebugBus };
